import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  LdsButton,
  LdsSearch,
  LdsSelect,
  LdsTable,
  LdsBadge,
  LdsLink,
  useToastContext,
} from "@elilillyco/ux-lds-react";

import ApprovedSpecialtiesTable from "../components/approvedSpecialties/ApprovedSpecialtiesTable";
import CLoader from "../components/shared/CLoader";
import infoToastConfig from "../components/shared/Toast";

import {
  FETCH_FILTER_LIST,
  FETCH_APPROVED_SPECIALTIES,
} from "../store/approvedSpecialties";

import exportExcelSheet from "../utils/overview/exportExcelSheet";
import ctx from "../plugins/axios";

import "../assets/scss/apprSpecialties/approvedSpecialties.scss";

const PRESETS = [
  { label: "Next 15 Days", days: 15 },
  { label: "Next 30 Days", days: 30 },
  { label: "Next 90 Days", days: 90 },
];

const ApprovedSpec = () => {
  const [activeTab, setActiveTab] = useState("approved");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedIndication, setSelectedIndication] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedThpc, setSelectedThpc] = useState("");
  const [selectedDsst, setSelectedDsst] = useState("");
  const [selectedMdpr, setSelectedMdpr] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [searchText, setSearchText] = useState("");
  const [firstRender, setFirstRender] = useState(true);
  const [visibleSpecialties, setVisibleSpecialties] = useState([]);

  // Expiration Date tab state
  const today = moment().format("YYYY-MM-DD");
  const [expLoading, setExpLoading] = useState(false);
  const [allLists, setAllLists] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(moment().add(30, "days").format("YYYY-MM-DD"));
  const [activePreset, setActivePreset] = useState(30);

  const { addToast } = useToastContext();

  const getTableData = (searchValue) => {
    return {
      brand: selectedBrand === null ? "" : selectedBrand,
      indication: selectedIndication === null ? "" : selectedIndication,
      thpc: selectedThpc === null ? "" : selectedThpc,
      dsst: selectedDsst === null ? "" : selectedDsst,
      mdpr: selectedMdpr === null ? "" : selectedMdpr,
      quarter: selectedQuarter === null ? "" : selectedQuarter,
      department: selectedDepartment === null ? "" : selectedDepartment,
      searchKeyword: searchValue === undefined ? searchText : searchValue,
    };
  };

  const dispatch = useDispatch();
  const {
    filterBrand,
    filterIndication,
    filterQuarter,
    filterDepartment,
    loading,
    approvedSpecialties,
    filterThpc,
    filterDsst,
    filterMdpr,
  } = useSelector(({ approvedSpecialties }) => approvedSpecialties);

  const getOptions = (options) => {
    const newOptions = options.map((option) => ({
      value: option,
      label: option,
    }));
    const emptyOption = { label: "Select", value: "" };
    newOptions.unshift(emptyOption);
    return newOptions;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(FETCH_FILTER_LIST());
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    setFirstRender(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!firstRender) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    }
  }, [selectedBrand, selectedIndication, selectedQuarter, selectedDepartment, selectedThpc, selectedDsst, selectedMdpr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tempSpecialties = JSON.parse(JSON.stringify(approvedSpecialties));
    if (tempSpecialties.length) {
      tempSpecialties.forEach((specialty) => {
        if (specialty.department_field === "RETAIL") {
          specialty.department_field = "COMMERCIAL";
        }
      });
    }
    setVisibleSpecialties(tempSpecialties);
  }, [approvedSpecialties]);

  // Expiration Date tab data fetching
  const fetchExpirationLists = useCallback(async () => {
    setExpLoading(true);
    try {
      const { data } = await ctx.get(
        "/specialty_list?brand=&thpc_nm=&user_brands="
      );
      if (data?.data) {
        const current = data.data.current_data || [];
        const historical = data.data.history_data || [];
        setAllLists([...current, ...historical]);
      }
    } catch (err) {
      console.error("Failed to fetch specialty lists", err);
    } finally {
      setExpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "expiration" && allLists.length === 0) {
      fetchExpirationLists();
    }
  }, [activeTab, fetchExpirationLists, allLists.length]);

  const applyPreset = (days) => {
    setActivePreset(days);
    setStartDate(today);
    setEndDate(moment().add(days, "days").format("YYYY-MM-DD"));
  };

  const filteredExpirationLists = allLists.filter((item) => {
    if (!item.expiration_date) return false;
    if ((item.list_status || "").toUpperCase() !== "APPROVED") return false;
    const exp = moment(item.expiration_date);
    return (
      exp.isSameOrAfter(moment(startDate).startOf("day")) &&
      exp.isSameOrBefore(moment(endDate).endOf("day"))
    );
  });

  const getDaysUntilExpiry = (expirationDate) => {
    const diff = moment(expirationDate).diff(moment().startOf("day"), "days");
    if (diff < 0) return "Expired";
    if (diff === 0) return "Expires today";
    return `${diff} day${diff !== 1 ? "s" : ""}`;
  };

  const getExpiryClass = (expirationDate) => {
    const diff = moment(expirationDate).diff(moment().startOf("day"), "days");
    if (diff < 0) return "expired";
    if (diff <= 15) return "expiring-soon";
    return "valid";
  };

  const submitSearch = () => {
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
  };

  const clearAllFilters = () => {
    setSelectedBrand("");
    setSelectedIndication("");
    setSelectedQuarter("");
    setSelectedDepartment("");
    setSearchText("");

    // fetching data here if only search field was set else fetching it in useEffect
    if (
      selectedBrand === "" &&
      selectedIndication === "" &&
      selectedQuarter === "" &&
      selectedDepartment === "" &&
      selectedThpc === "" &&
      selectedDsst === "" &&
      selectedMdpr === "" &&
      searchText !== ""
    ) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData("")));
    }
  };

  const convertDateFormat = (dateObj) => {
    return dateObj ? moment(dateObj).format("MMMM DD, YYYY") : "";
  };

  const exportData = () => {
    const dataToExport = [];

    if (!Array.isArray(visibleSpecialties)) {
      addToast(
        infoToastConfig("Failed to export the specialties data.", "error")
      );
      return;
    }

    if (!visibleSpecialties.length) {
      addToast(infoToastConfig("No specialty data to export.", "warning"));
      return;
    }

    visibleSpecialties.forEach((specialty) => {
      dataToExport.push({
        Title: specialty.list_title || "",
        TherapeuticArea: specialty.thpc_nm || "",
        DiseaseState: specialty.dsst_nm || "",
        Molecule: specialty.mdpr_nm || "",
        Brand: specialty.brand_nm || "",
        Indication: specialty.indication_nm || "",
        Quarter: specialty.list_quarter || "",
        Department: specialty.department_field || "",
        "Approved Date": convertDateFormat(specialty.updated_date),
      });
    });

    exportExcelSheet(dataToExport, "Approved Specialities");
  };

  return (
    <>
      <section className="section">
        <div className="approved-page">
          {/* LDS Tab Group - above header */}
          <div className="lds-card lds-tab-group approved-tab-group" aria-label="Approved specialities tabs">
            <div className="lds-tab-header align-left">
              <div className="tab-btn-container">
                <button
                  className={`lds-button-clear-style tab-btn focus-3px ${activeTab === "approved" ? "active" : ""}`}
                  onClick={() => setActiveTab("approved")}
                >
                  Approved Specialities
                </button>
                <button
                  className={`lds-button-clear-style tab-btn focus-3px ${activeTab === "expiration" ? "active" : ""}`}
                  onClick={() => setActiveTab("expiration")}
                >
                  Expiration Date
                </button>
              </div>
              <div className="lds-tab-drawer">
                <button className="lds-button-clear-style tab-btn tab-drawer-btn active focus-3px">
                  <span className="lds-tab-drawer-label">
                    {activeTab === "approved" ? "Approved Specialities" : "Expiration Date"}
                  </span>
                </button>
              </div>
            </div>
            <div className="lds-tab-container tab-padded">
              {/* Tab 1: Approved Specialities */}
              <div className={`lds-tab ${activeTab === "approved" ? "active" : ""}`} id="tab-approved">
                <div className="mt-3">
                  <div className="row">
                    <div className="col-9">
                      <h2 className="pl-4">Approved Specialities</h2>
                    </div>
                    <div className="col-3 export-button-col">
                      <LdsButton
                        classes={`button-icon-left mr-3 ${
                          loading ? "outline secondary" : "blue-btn"
                        }`}
                        icon="DownloadSimple"
                        iconPosition="before"
                        onClick={exportData}
                        disabled={!!loading}
                      >
                        Export
                      </LdsButton>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <LdsSearch
                      id={"searchSpecialty"}
                      onChange={setSearchText}
                      value={searchText}
                      onSubmit={submitSearch}
                    />
                  </div>
                  <div className="col-6">
                    <LdsButton onClick={clearAllFilters}>Clear All</LdsButton>
                  </div>
                </div>
                {filterBrand.length > 0 &&
                  filterDepartment.length > 0 &&
                  filterQuarter.length > 0 &&
                  filterIndication.length > 0 &&
                  filterThpc.length > 0 &&
                  filterDsst.length > 0 &&
                  filterMdpr.length > 0 && (
                    <>
                      <div className="row filter">
                        <div className="col-3">
                          <LdsSelect
                            id="select-product"
                            name="select-product"
                            label="Therapeutic Area"
                            value={selectedThpc}
                            options={getOptions(filterThpc)}
                            onChange={(option) => {
                              setSelectedThpc(option.value);
                            }}
                          ></LdsSelect>
                        </div>
                        <div className="col-3">
                          <LdsSelect
                            id="select-indication"
                            name="select-indication"
                            label="Disease State"
                            value={selectedDsst}
                            options={getOptions(filterDsst)}
                            onChange={(option) => setSelectedDsst(option.value)}
                          ></LdsSelect>
                        </div>
                        <div className="col-3">
                          <LdsSelect
                            id="select-quarter"
                            name="select-quarter"
                            label="Molecule"
                            value={selectedMdpr}
                            options={getOptions(filterMdpr)}
                            onChange={(option) => setSelectedMdpr(option.value)}
                          ></LdsSelect>
                        </div>
                        <div className="col-3">
                          <LdsSelect
                            id="select-indication-filter"
                            name="select-indication-filter"
                            label="Indication"
                            value={selectedIndication}
                            options={getOptions(filterIndication)}
                            onChange={(option) => setSelectedIndication(option.value)}
                          ></LdsSelect>
                        </div>
                      </div>
                      <div className="row filter">
                        <div className="col-3">
                          <LdsSelect
                            id="select-brand"
                            name="select-brand"
                            label="Brand"
                            value={selectedBrand}
                            options={getOptions(filterBrand)}
                            onChange={(option) => {
                              setSelectedBrand(option.value);
                            }}
                          ></LdsSelect>
                        </div>
                        <div className="col-3">
                          <LdsSelect
                            id="select-quarter-filter"
                            name="select-quarter-filter"
                            label="Quarter"
                            value={selectedQuarter}
                            options={getOptions(filterQuarter)}
                            onChange={(option) => setSelectedQuarter(option.value)}
                          ></LdsSelect>
                        </div>
                        <div className="col-3">
                          <LdsSelect
                            id="select-department"
                            name="select-department"
                            label="Department"
                            value={selectedDepartment}
                            options={getOptions(filterDepartment)}
                            onChange={(option) => setSelectedDepartment(option.value)}
                          ></LdsSelect>
                        </div>
                        <div className="col-3"></div>
                      </div>
                    </>
                  )}
                {loading ? (
                  <CLoader size={90} />
                ) : (
                  <div className="apprSpecTable">
                    <ApprovedSpecialtiesTable
                      visibleSpecialties={visibleSpecialties}
                      setVisibleSpecialties={setVisibleSpecialties}
                      convertDateFormat={convertDateFormat}
                    />
                  </div>
                )}
              </div>

              {/* Tab 2: Expiration Date */}
              <div className={`lds-tab ${activeTab === "expiration" ? "active" : ""}`} id="tab-expiration">
                <div className="mt-3">
                  <div className="row">
                    <div className="col-12">
                      <h2 className="pl-4">Expiration Date</h2>
                    </div>
                  </div>
                </div>
                <div className="exp-filter-row">
                  <div className="exp-date-pickers">
                    <div className="exp-date-field">
                      <label htmlFor="exp-start-date" className="exp-date-label">
                        From
                      </label>
                      <div className="exp-date-wrapper">
                        <span className="exp-date-icon">&#128197;</span>
                        <input
                          id="exp-start-date"
                          type="date"
                          value={startDate}
                          min={today}
                          max={endDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setActivePreset(null);
                          }}
                          className="exp-date-input"
                        />
                      </div>
                    </div>
                    <span className="exp-divider">&#8594;</span>
                    <div className="exp-date-field">
                      <label htmlFor="exp-end-date" className="exp-date-label">
                        To
                      </label>
                      <div className="exp-date-wrapper">
                        <span className="exp-date-icon">&#128197;</span>
                        <input
                          id="exp-end-date"
                          type="date"
                          value={endDate}
                          min={startDate > today ? startDate : today}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setActivePreset(null);
                          }}
                          className="exp-date-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="exp-preset-buttons">
                    <div className="exp-preset-group">
                      <span className="exp-preset-label">Quick Select</span>
                      <div className="exp-preset-btns">
                        {PRESETS.map(({ label, days }) => (
                          <LdsButton
                            key={days}
                            classes={activePreset === days ? "blue-btn" : ""}
                            onClick={() => applyPreset(days)}
                          >
                            {label}
                          </LdsButton>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  {expLoading ? (
                    <CLoader size={90} />
                  ) : (
                    <>
                      <p className="exp-results-count">
                        Showing{" "}
                        <strong>{filteredExpirationLists.length}</strong>{" "}
                        {filteredExpirationLists.length === 1 ? "list" : "lists"} expiring between{" "}
                        <strong>{moment(startDate).format("MMM DD, YYYY")}</strong>
                        {" "}and{" "}
                        <strong>{moment(endDate).format("MMM DD, YYYY")}</strong>
                      </p>

                      {filteredExpirationLists.length === 0 ? (
                        <div className="row">
                          <span className="center">
                            <h2>No lists expiring in the selected date range.</h2>
                          </span>
                        </div>
                      ) : (
                        <LdsTable id="expiration-date-table" flat>
                          <thead>
                            <tr>
                              <th>List Title</th>
                              <th>Brand/Molecule</th>
                              <th>Status</th>
                              <th>Expiration Date</th>
                              <th>Days Until Expiry</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredExpirationLists
                              .slice()
                              .sort((a, b) =>
                                moment(a.expiration_date).diff(moment(b.expiration_date))
                              )
                              .map((item, i) => (
                                <tr key={i}>
                                  <td>
                                    {item.uuid ? (
                                      <LdsLink href={`/details/${item.uuid}`}>
                                        {item.list_title || "—"}
                                      </LdsLink>
                                    ) : (
                                      item.list_title || "—"
                                    )}
                                  </td>
                                  <td>{item.brand_nm || item.mdpr_nm || "—"}</td>
                                  <td>
                                    {item.list_status ? (
                                      <LdsBadge className={
                                        item.list_status.toUpperCase() === "APPROVED"
                                          ? "green-btn"
                                          : "draft-badge"
                                      }>
                                        {item.list_status.toUpperCase()}
                                      </LdsBadge>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td>{moment(item.expiration_date).format("MMM DD, YYYY")}</td>
                                  <td>
                                    <span className={`expiration-tag ${getExpiryClass(item.expiration_date)}`}>
                                      {getDaysUntilExpiry(item.expiration_date)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </LdsTable>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ApprovedSpec;
